<template>

  <!-- Loader for Manual Entry -->
  <LoadingDiv v-if="!isReady" />

  <div class="auth-start-shell sba-slide-in" v-else>
    <template v-if="!enrolleeSearch">
      <form action="#">
      <div class="auth-start-card">
      <div class="auth-start-main" :class="{ 'has-error': error }">
        <div class="auth-start-copy">
          <span>Step 1</span>
          <h6>Enrollee Details</h6>
          <p>Enter the enrollee identity exactly as it should appear on the authorization request.</p>
        </div>
        <div class="auth-start-fields">
          <div class="auth-start-field">
            <div class="input-field auth-input-field">
              <input type="text" id="enrolleeId" v-model="enrolleeId" autocomplete="off" />
              <label for="enrolleeId">Enrollee ID</label>
            </div>
          </div>
          <div class="auth-start-field">
            <div class="input-field auth-input-field">
              <input type="text" id="enrolleeName" v-model="enrolleeName" autocomplete="off" />
              <label for="enrolleeName">Enrollee Name</label>
            </div>
          </div>
        </div>
        <div class="auth-start-error" v-if="error">
          <i class="material-icons">error_outline</i>
          <span>{{ error }}</span>
        </div>
      </div>
     
      <div class="auth-scheme-panel">
        <div class="auth-scheme-title">
          <span>Step 2</span>
          <h6>Scheme</h6>
        </div>
        <div class="auth-scheme-grid">
          <label class="auth-scheme-option" :class="{ 'is-selected': selectedScheme?.id === scheme.id }" v-for="scheme in schemes" :key="scheme.id">
            <input id="selectedScheme" v-model="selectedScheme" :value="scheme" type="radio" />
            <span>
              <strong>{{ scheme.name }}</strong>
              <small>{{ scheme.type == 'p' ? 'Private Scheme' : 'Social Scheme' }}</small>
            </span>
          </label>
        </div>
      </div>

      <div class="auth-start-submit">
        <button type="button" class="auth-next-button" :class="{
          disabled: !enrolleeId || !enrolleeName || !selectedScheme,
        }" @click="handleSearch()">
          <span>Next</span>
          <i class="material-icons" v-if="!isLoading">chevron_right</i>
        </button>
      </div>
      </div>
      </form>
    </template>

    <template v-else>
      <div class="card grey lighten-5 auth-start-search-card">
        <!-- Search -->
        <div class="card-panel" :class="{ 'red lighten-5': error, 'grey lighten-4': !error }">
          <h5>Enter Enrollee ID</h5>
          <br />
          <nav class="white">
            <div class="nav-wrapper">
              <form @submit.prevent="handleSearch()">
                <div class="input-field blue-text text-darken-2">
                  <input type="search" v-model="enrolleeId" placeholder="Enrollee ID" @focus="handleClear" />
                  <label class="label-icon" for="search"><i class="material-icons black-text">search</i></label>
                  <i class="material-icons" @click.prevent="handleSearch()">send</i>
                </div>
              </form>
            </div>
          </nav>
        </div>

        <div class="progress" v-if="isLoading">
          <div class="indeterminate"></div>
        </div>

        <!-- Confirmation for more than multiple enrollees -->
        <div class="card yellow lighten-5 grey-text text-darken-2 auth-start-result-card" v-for="enrollee in enrolleeGroup" :key="enrollee.id">
          <div class="card-content">
            <div class="row">
              <div class="col s10">
                <span class="new badge left grey darken-2" data-badge-caption="Scheme">{{
                  enrollee?.scheme.name.toUpperCase()
                }}</span>
                <br />
                <h5>{{ capWords(enrollee?.name) }} <small>({{ enrollee?.enrollee_number }})</small></h5>
              </div>
              <div class="col s2">
                <a class="btn-floating btn-large waves-effect waves-light light-blue lighten-2 pulse right"><i
                    class="material-icons" @click.prevent="handleStart(enrollee)" title="details">chevron_right</i></a>
              </div>
            </div>
          </div>
        </div>

        <br />
      </div>
    </template>

  </div>

</template>

<script>
import { ref, watchEffect, onMounted } from "vue";
import { useStore } from "vuex";
import { capWordsFx } from "@/composables/fxLibrary/coreFx";
import getItems from "@/composables/getItems";
import getItemsOfItems from "@/composables/getItemsOfItems";
import LoadingDiv from "@/components/LoadingDiv.vue";
import { getCachedServerDate } from "@/composables/requestOptimization";
import { ID_NUMBER } from '@/laravel/config';

export default {
  name: "StartAuthComponent",
  components: { LoadingDiv },
  emits: ["next"],
  setup(props, context) {
    const enrolleeSearch = ref(false); //use manual for now
    const response = ref(null);
    const enrolleeGroup = ref(null);
    const enrolleeId = ref(null);
    const enrolleeName = ref(null);
    const selectedScheme = ref(null);
    const serverDate = ref(null);
    const error = ref(false);
    const isReady = ref(enrolleeSearch.value);
    const isLoading = ref(false);

    // store
    const store = useStore();


    // display
    onMounted(() => {
      // get standalone server date
      if (!enrolleeSearch.value) {
        (async () => {
        const { res } = await getCachedServerDate(getItems);

        if (res.value) {
          serverDate.value = res.value.data.current_date_full;
        }
        // open page
        isReady.value = true;
      })();

      }
      
    });


    //from functions
    const capWords = (val) => {
      return capWordsFx(val);
    };

    // get function
    const getSearchItemsFromApi = (table, match) => {
      // loading starts
      isLoading.value = true;
      // clear error
      error.value = null;

      (async () => {
        const { res } = await getItemsOfItems(table, match);

        if (res.value && res.value.status) {
          // set server date
          serverDate.value = res.value.server;
          // display options
          if (res.value.data.length === 1) {
            response.value = res.value.data[0];
          } else {
            enrolleeGroup.value = res.value.data;
          }
        } else {
          error.value = `Enrollee not on current List!`;
          response.value = null; // clear standing response
        }

        // page is ready, loading is complete
        isLoading.value = false;
      })();
    };

    // search
    const handleSearch = () => {

      if (enrolleeSearch.value) {

        // loading starts
        isLoading.value = true;
        // val
        let val = `q=${enrolleeId.value.trim()}`;
        // search: 0 = provider_id
        getSearchItemsFromApi("operator/enrollee/encounter/checker", `0?${val}`);


      } else {

        // build pseudo enrollee
        let newVal = {
          id: Date.now(),
          payer_id: ID_NUMBER,
          name: enrolleeName.value,
          enrollee_number: enrolleeId.value,
          dob: null,
          sex: null,
          client_name: null,
          scheme: selectedScheme.value,
          scheme_id: selectedScheme.value?.id,
          profile_img: null,
        };

        // push start
        handleStart(newVal);

      }

    };

    // start
    const handleStart = (val) => {
      // set
      response.value = val;
    }

    // clear
    const handleClear = () => {
      enrolleeGroup.value = [];
      enrolleeId.value = null;
      response.value = null;
      error.value = false;
    };

    // next
    watchEffect(() => {
      if (response.value) {
        context.emit("next", {
          value: response.value,
          serverDate: serverDate.value,
        });
      }
    });

    return {
      capWords,
      handleStart,
      handleSearch,
      handleClear,
      error,
      isReady,
      isLoading,
      enrolleeGroup,
      enrolleeId,
      enrolleeName,
      selectedScheme,
      enrolleeSearch,
      schemes: store.state.user.schemes
    };
  },
};
</script>

<style scoped>
.auth-start-shell {
  margin-top: 16px;
}

.auth-start-card,
.auth-start-search-card,
.auth-start-result-card {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.auth-start-card {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: none;
  overflow: hidden;
}

.auth-start-main {
  background: #ffffff;
  display: grid;
  gap: 24px;
  grid-template-columns: minmax(220px, 0.42fr) minmax(0, 1fr);
  padding: 24px;
}

.auth-start-main.has-error {
  background: #fff8f8;
}

.auth-start-copy span,
.auth-scheme-title span {
  color: #009688;
  display: block;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.auth-start-copy h6,
.auth-scheme-title h6 {
  color: #263238;
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
}

.auth-start-copy p {
  color: #607d8b;
  line-height: 1.45;
  margin: 8px 0 0;
}

.auth-start-fields {
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(180px, 0.72fr) minmax(240px, 1fr);
}

.auth-start-field {
  background: #f8fafb;
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  min-width: 0;
  padding: 6px 12px 0;
}

.auth-input-field {
  margin: 0;
}

.auth-input-field input {
  border-bottom-color: transparent !important;
  color: #263238;
  font-weight: 700;
  margin-bottom: 0 !important;
}

.auth-input-field input:focus {
  border-bottom-color: #00897b !important;
  box-shadow: 0 1px 0 0 #00897b !important;
}

.auth-start-error {
  align-items: center;
  background: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 6px;
  color: #b71c1c;
  display: flex;
  font-weight: 700;
  gap: 8px;
  grid-column: 2;
  padding: 10px 12px;
}

.auth-start-error i {
  font-size: 18px;
}

.auth-scheme-panel {
  background: #f8fafb;
  border-top: 1px solid #e1e8eb;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(220px, 0.42fr) minmax(0, 1fr);
  padding: 20px 24px;
}

.auth-scheme-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.auth-scheme-option {
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid #dfe7ea;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  gap: 10px;
  min-height: 74px;
  padding: 14px;
}

.auth-scheme-option.is-selected {
  background: #e8f3f2;
  border-color: #00897b;
}

.auth-scheme-option input {
  margin-top: 4px;
}

.auth-scheme-option span {
  color: #263238;
  line-height: 1.3;
}

.auth-scheme-option strong {
  display: block;
  font-weight: 700;
}

.auth-scheme-option small {
  color: #607d8b;
  display: block;
  font-weight: 700;
  margin-top: 5px;
}

.auth-start-submit {
  align-items: center;
  background: #f5f5f5;
  border-top: 1px solid #e1e8eb;
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
}

.auth-next-button {
  align-items: center;
  background: #263238;
  border: 1px solid #263238;
  border-radius: 6px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  font-weight: 700;
  gap: 8px;
  min-height: 42px;
  padding: 0 16px;
}

.auth-next-button i {
  font-size: 20px;
}

.auth-next-button.disabled {
  background: #cfd8dc;
  border-color: #cfd8dc;
  color: #607d8b;
  pointer-events: none;
}

.progress {
  margin-top: -15px;
}

@media only screen and (max-width: 992px) {
  .auth-start-main,
  .auth-scheme-panel,
  .auth-start-fields {
    grid-template-columns: 1fr;
  }

  .auth-start-error {
    grid-column: auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .auth-start-card,
  .auth-start-search-card,
  .auth-start-result-card {
    animation: none;
  }
}
</style>
